import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { fetchAdminGameAnalytics } from './adminAnalyticsService';
import { USD_RATES } from '../utils/currency';

export { USD_RATES };

const TZ = 'Asia/Beirut';

// Revenue share applied to BetConstruct sports GGR.
export const SPORT_REVENUE_SHARE = 0.03;

// Only BetConstruct counts towards this share; GR8 is excluded.
const BOOK = 'betconstruct';

// The pre-aggregated GameAnalytics table starts at July 2026, so earlier
// months cannot be reported from it.
const FIRST_MONTH = { year: 2026, month: 7 };

// USD, TND, LBP, and KWD count towards this revenue share; BetConstruct activity
// in other currencies (TRY, IQD) is excluded.
export const TRACKED_CURRENCIES = ['USD', 'TND', 'LBP', 'KWD'];

const pad2 = (n) => String(n).padStart(2, '0');

const beirutWall = (year, month, day, hour = 0, minute = 0) =>
  fromZonedTime(`${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:00.000`, TZ);

export const monthLabel = (year, month) =>
  formatInTimeZone(beirutWall(year, month, 1), TZ, 'MMMM yyyy');

export const monthKey = (year, month) => `${year}-${pad2(month)}`;

/** Month boundaries in Beirut time; the end is the last millisecond. */
export const monthRange = (year, month) => {
  const start = beirutWall(year, month, 1);
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return { start, end: new Date(beirutWall(nextYear, nextMonth, 1).getTime() - 1) };
};

/** Selectable months, newest first, from July 2026 through the current month. */
export const listAvailableMonths = () => {
  const [nowYear, nowMonth] = formatInTimeZone(new Date(), TZ, 'yyyy-MM').split('-').map(Number);
  const months = [];
  let year = FIRST_MONTH.year;
  let month = FIRST_MONTH.month;
  while (year < nowYear || (year === nowYear && month <= nowMonth)) {
    months.push({ key: monthKey(year, month), label: monthLabel(year, month), year, month });
    if (month === 12) {
      month = 1;
      year += 1;
    } else {
      month += 1;
    }
  }
  return months.reverse();
};

/**
 * BetConstruct sports GGR for one month, per currency, with the 3% share.
 * Reads the pre-aggregated GameAnalytics table through /adminanalytics rather
 * than the Analytics table, which cannot separate BetConstruct from GR8.
 */
export const fetchBetConstructRevenueShare = async ({ year, month }) => {
  const { start, end } = monthRange(year, month);

  const response = await fetchAdminGameAnalytics({
    startDate: start,
    endDate: end,
    currency: null,
    tab: 'sport'
  });

  const rows = (response?.sports?.books || [])
    .filter((row) => row.book === BOOK && TRACKED_CURRENCIES.includes(row.currency));

  // Always report every tracked currency so a month with no activity in one of
  // them still shows a zero row rather than silently dropping it.
  const currencies = TRACKED_CURRENCIES.map((code) => {
    const row = rows.find((r) => r.currency === code);
    const ggr = Number(row?.ggr) || 0;
    const share = ggr * SPORT_REVENUE_SHARE;
    return {
      currency: code,
      ggr,
      share,
      shareUsd: share / USD_RATES[code],
      turnover: Number(row?.betAmount) || 0,
      bets: Number(row?.betCount) || 0,
      players: Number(row?.uniquePlayers) || 0
    };
  });

  return {
    monthKey: monthKey(year, month),
    label: monthLabel(year, month),
    startDate: start,
    endDate: end,
    currencies,
    totalUsd: currencies.reduce((sum, c) => sum + c.shareUsd, 0),
    ingest: response?.ingest || null
  };
};
